'use strict'

/**
 * Display palette + chip / icon / label dictionaries for master-lot UI.
 *
 * Answers: "how do I render this lot/unit/milestone/disposition?"
 *
 * Cosmetic concerns previously duplicated across the four `MasterLot*`
 * views and the `Mlb*` components: status-chip palette, metal-chip palette,
 * date / date-time formatters, manifest-unit kind vocabulary, milestone
 * actor catalog, settlement-doc MIME → icon mapping, settlement
 * disposition labels.
 *
 * Pure read-side helpers — no compute, no side effects.
 */

import {format, isValid} from 'date-fns'
import {formatWithGrouping} from '@/util/formatting'
import {MASTER_LOT_STATUSES, MILESTONE_KINDS, UNIT_KINDS} from '../constants'

// ============================================================================
// Status + metal palette
// ============================================================================

const STATUS_COLORS = Object.freeze({
  [MASTER_LOT_STATUSES.DRAFT]: 'grey lighten-2',
  [MASTER_LOT_STATUSES.CONFIRMED]: 'blue lighten-5',
  [MASTER_LOT_STATUSES.SHIPPED]: 'blue lighten-4',
  [MASTER_LOT_STATUSES.RECEIVED]: 'blue lighten-3',
  [MASTER_LOT_STATUSES.MELTED]: 'amber lighten-3',
  [MASTER_LOT_STATUSES.PIN_RETURNED]: 'amber lighten-2',
  [MASTER_LOT_STATUSES.ASSAYED]: 'light-green lighten-3',
  [MASTER_LOT_STATUSES.SETTLED]: 'success',
  [MASTER_LOT_STATUSES.CANCELLED]: 'red lighten-4',
})

const METAL_COLORS = Object.freeze({
  au: 'amber darken-2',
  ag: 'blue-grey',
  pt: 'grey darken-1',
  pd: 'deep-purple',
  cu: 'orange darken-3',
  // Display fallbacks for unnamed metals → grey via metalColor().
  other: 'brown',
})

const METAL_FULL_NAMES = Object.freeze({
  au: 'Gold',
  ag: 'Silver',
  pt: 'Platinum',
  pd: 'Palladium',
  cu: 'Copper',
  other: 'Other',
})

// Human label for a master-lot lifecycle status; most are a single capitalized
// word, but pin_returned needs the underscore spelled out.
const STATUS_LABELS = Object.freeze({
  [MASTER_LOT_STATUSES.DRAFT]: 'Draft',
  [MASTER_LOT_STATUSES.CONFIRMED]: 'Confirmed',
  [MASTER_LOT_STATUSES.SHIPPED]: 'Shipped',
  [MASTER_LOT_STATUSES.RECEIVED]: 'Received',
  [MASTER_LOT_STATUSES.MELTED]: 'Melted',
  [MASTER_LOT_STATUSES.PIN_RETURNED]: 'Pin returned',
  [MASTER_LOT_STATUSES.ASSAYED]: 'Assayed',
  [MASTER_LOT_STATUSES.SETTLED]: 'Settled',
  [MASTER_LOT_STATUSES.CANCELLED]: 'Cancelled',
})

export const masterLotStatusLabel = (status) => STATUS_LABELS[status] || status || '--'

export const statusColor = (status) => STATUS_COLORS[status] || 'grey lighten-3'

// Settled chip uses the dark `success` swatch so its label needs to be white;
// every other chip background is light-toned and reads better with darker text.
export const statusTextColor = (status) => (status === MASTER_LOT_STATUSES.SETTLED ? 'white' : 'grey darken-3')

export const metalColor = (metal) => METAL_COLORS[String(metal).toLowerCase()] || 'grey'

/**
 * Vuetify text-color classes derived from `metalColor` background tokens
 * (e.g. `'amber darken-2'` → `['amber--text', 'text--darken-2']`).
 * Module-private — consumers use `metalEmphasisClasses`.
 */
const metalTextClasses = (metal) => {
  const [color, shade] = metalColor(metal).split(' ')
  return [
    `${color}--text`,
    shade ? `text--${shade}` : null,
  ].filter(Boolean)
}

/**
 * Lowercase / trim / dedupe metal symbols while preserving first-seen order.
 * Single home for contract paidMetals and lot.metals canonicalization.
 */
export const normalizeMetalSymbols = (list) => {
  const seen = new Set()
  const metals = []
  for (const raw of Array.isArray(list) ? list : []) {
    const metal = String(raw || '').toLowerCase().trim()
    if (!metal || seen.has(metal)) continue
    seen.add(metal)
    metals.push(metal)
  }
  return metals
}

/**
 * Bold + metal-colored text for paid metals; subdued grey for everything else.
 * Accepts an array or Set of paid metal symbols. Shared by signature labels
 * and purity cells.
 */
export const metalEmphasisClasses = (metal, paidMetals) => {
  const paid = paidMetals instanceof Set
    ? paidMetals
    : new Set(normalizeMetalSymbols(paidMetals))
  const key = String(metal || '').toLowerCase().trim()
  if (paid.has(key)) {
    return ['font-weight-bold', ...metalTextClasses(key)]
  }
  return ['grey--text']
}

/**
 * Contract-paid metals first (stable paid order), then remaining lot metals
 * sorted — one normalize pass. Shared by the collapsed lot signature.
 */
export const lotMetalSignature = (lotMetals, paidMetals) => {
  const normalizedLot = normalizeMetalSymbols(lotMetals)
  const paidList = normalizeMetalSymbols(paidMetals)
  const lotSet = new Set(normalizedLot)
  const paidSet = new Set(paidList)
  const paid = paidList.filter((metal) => lotSet.has(metal))
  const supplemental = normalizedLot.filter((metal) => !paidSet.has(metal)).sort()
  return [...paid, ...supplemental]
}

/**
 * Uppercase chip-style label for a paid-metal symbol (e.g. `'au'` → `'AU'`).
 * Single home so every place that renders a metal as a label agrees on
 * casing — chips, diff rows, warning messages.
 */
export const metalLabel = (metal) => String(metal).toUpperCase()

/**
 * Human metal name for catalog chips (`au` → `Gold`, `other` → `Other`).
 * Falls back to uppercase symbol when unknown.
 */
export const metalFullName = (metal) => {
  const key = String(metal || '').toLowerCase()
  if (!key) return ''
  return METAL_FULL_NAMES[key] || metalLabel(key)
}

/**
 * Normalize Product.metalSymbol / metalType for display + purity keys.
 * - `XAU` / `XAG` / … → `au` / `ag` / …
 * - lone `X` or `X - OTHER - …` → `other` (must NOT strip to empty — that
 *   produced blank grey chips that looked like silver)
 * @param {object|string|null|undefined} productOrSymbol
 * @returns {string|null}
 */
export const normalizeProductMetalKey = (productOrSymbol) => {
  const raw = typeof productOrSymbol === 'string'
    ? productOrSymbol
    : (productOrSymbol?.metalSymbol || productOrSymbol?.metalType || '')
  const text = String(raw || '').trim()
  if (!text) return null

  const iso = text.match(/^x([a-z]{2})\b/i)
  if (iso) return iso[1].toLowerCase()

  const token = text.split(/[\s/_-]+/)[0]
  if (/^x$/i.test(token)) return 'other'
  if (/^[a-z]{2}$/i.test(token)) return token.toLowerCase()
  return token ? token.toLowerCase() : null
}

// ============================================================================
// Numeric formatters
// ----------------------------------------------------------------------------
// Shared by the settlement metal row (live preview) and the settlement card's
// metal table (recorded summary) so both render numbers and variances the
// same way — dash for missing/non-finite, signed percent for variance.
// ============================================================================

// Signed percentage with a "—" dash guard for missing/non-finite input, e.g.
// `+1.25%` / `-0.40%` / `—`. Coerce-once-then-guard so Infinity and
// non-numeric strings dash out instead of leaking through `.toFixed`.
export const formatVariancePct = (value) => {
  const pct = Number(value)
  if (value === null || value === undefined || !Number.isFinite(pct)) return '—'
  const sign = pct >= 0 ? '+' : ''
  return `${sign}${pct.toFixed(2)}%`
}

// Grouped number with a "—" dash guard for missing/non-finite input
// (Infinity / non-numeric strings included).
export const formatNumberOrDash = (value, digits = 0) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—'
  return formatWithGrouping(value, digits)
}

// ============================================================================
// Date formatters
// ============================================================================

const parseIso = (iso) => {
  if (!iso) return null
  const parsed = new Date(iso)
  return isValid(parsed) ? parsed : null
}

export const formatLotDate = (iso) => {
  const parsed = parseIso(iso)
  return parsed ? format(parsed, 'MM/dd/yyyy') : '--'
}

export const formatLotDateTime = (iso) => {
  const parsed = parseIso(iso)
  return parsed ? format(parsed, 'MMM d, yyyy h:mm a') : '--'
}

// ============================================================================
// Unit-kind vocabulary
// ----------------------------------------------------------------------------
// A manifest unit's `kind` is one of UNIT_KINDS. Both `SourceLotPicker` (bars/
// pins only) and `ManifestPanel` (bars/pins + catalog) hung the same
// icon/colour/label dictionaries off themselves; these helpers are the single
// home for that vocabulary so a future kind shows up in both views with one
// edit.
// ============================================================================

const KIND_ICON = Object.freeze({
  [UNIT_KINDS.BAR]: 'mdi-cube-outline',
  [UNIT_KINDS.HOUSE_PIN]: 'mdi-pin-outline',
  [UNIT_KINDS.CUSTOMER_PIN]: 'mdi-pin-outline',
  [UNIT_KINDS.MANUAL]: 'mdi-pencil-circle-outline',
  [UNIT_KINDS.CATALOG]: 'mdi-barcode',
})

const KIND_ICON_COLOR = Object.freeze({
  [UNIT_KINDS.BAR]: '',
  [UNIT_KINDS.HOUSE_PIN]: 'blue-grey',
  [UNIT_KINDS.CUSTOMER_PIN]: 'deep-orange',
  [UNIT_KINDS.MANUAL]: 'warning',
  [UNIT_KINDS.CATALOG]: 'teal',
})

const KIND_LABEL = Object.freeze({
  [UNIT_KINDS.BAR]: 'Bar',
  [UNIT_KINDS.HOUSE_PIN]: 'House pin',
  [UNIT_KINDS.CUSTOMER_PIN]: 'Customer pin',
  [UNIT_KINDS.MANUAL]: 'Manual',
  [UNIT_KINDS.CATALOG]: 'Catalog',
})

export const unitKindIcon = (kind) => KIND_ICON[kind] || 'mdi-cube-outline'

export const unitKindIconColor = (kind) => KIND_ICON_COLOR[kind] || ''

export const unitKindLabel = (kind) => KIND_LABEL[kind] || kind || '--'

export const unitWeightDisplayContext = (unit) => (
  [UNIT_KINDS.HOUSE_PIN, UNIT_KINDS.CUSTOMER_PIN].includes(unit?.kind) ? 'pin' : 'bar'
)

/**
 * Source-unit NetSuite serial/SKU. Catalog rows use their product SKU through
 * `unitLabel`.
 */
export const sourceUnitSerialLabel = (unit) => {
  if (!unit || unit.kind === UNIT_KINDS.CATALOG) return ''
  return unit.serialNumber || unit.sku || ''
}

/**
 * Primary display name for a manifest unit. Source units prefer their shared
 * serial/SKU; incomplete rows fall back to kind and bar number.
 */
export const unitLabel = (unit) => {
  if (!unit) return ''
  if (unit.kind === UNIT_KINDS.CATALOG) {
    return unit.sku || unit.name || unit.id || ''
  }
  const serial = sourceUnitSerialLabel(unit)
  if (serial) return serial
  const barNumber = unit.barNumber ?? '?'
  switch (unit.kind) {
    case UNIT_KINDS.BAR:          return `Bar #${barNumber}`
    case UNIT_KINDS.HOUSE_PIN:    return `House pin (bar #${barNumber})`
    case UNIT_KINDS.CUSTOMER_PIN: return `Customer pin (bar #${barNumber})`
    default:                      return unit.id || ''
  }
}

// ============================================================================
// Milestone icons
// ----------------------------------------------------------------------------
// Single home for the kind → icon mapping shared by the contract-accountability
// card's expected-milestones timeline (and any other surface that renders a
// per-milestone glyph).
// ============================================================================

const MILESTONE_ICONS = Object.freeze({
  [MILESTONE_KINDS.SHIPPED]: 'mdi-truck',
  [MILESTONE_KINDS.RECEIVED]: 'mdi-package-down',
  [MILESTONE_KINDS.MELTED]: 'mdi-fire',
  [MILESTONE_KINDS.PIN_RETURNED]: 'mdi-pin',
  [MILESTONE_KINDS.ASSAYED]: 'mdi-flask',
  [MILESTONE_KINDS.SETTLED]: 'mdi-check-decagram',
})

export const milestoneIcon = (kind) => MILESTONE_ICONS[kind] || 'mdi-circle-medium'

// Terse human label for a milestone kind. Used by surfaces that render a kind
// without a pre-attached `.label` (e.g. the contract-switch diff's
// "new expected milestones" chips).
const MILESTONE_LABELS = Object.freeze({
  [MILESTONE_KINDS.SHIPPED]: 'Shipped',
  [MILESTONE_KINDS.RECEIVED]: 'Received',
  [MILESTONE_KINDS.MELTED]: 'Melted',
  [MILESTONE_KINDS.PIN_RETURNED]: 'Pin returned',
  [MILESTONE_KINDS.ASSAYED]: 'Assayed',
  [MILESTONE_KINDS.SETTLED]: 'Settled',
})

export const milestoneLabel = (kind) => MILESTONE_LABELS[kind] || kind || '--'

/**
 * Per-metal purity (percent, 0–100) for display purposes. Returns a finite
 * number or 0 when missing/non-finite — matching the display semantics
 * `ManifestPanel.purityFor` and `SourceLotPicker.hasPurity` were encoding
 * inline.
 */
export const unitDisplayPurity = (unit, metal) => {
  const purityValue = unit?.purity?.[metal]
  return typeof purityValue === 'number' && Number.isFinite(purityValue) ? purityValue : 0
}

/**
 * Whether the unit has a non-zero purity for the given metal — i.e. whether
 * the purity chip / row should render. Mirrors the previous component-local
 * `hasPurity` semantics in both Mlb* components.
 */
export const hasDisplayablePurity = (unit, metal) => unitDisplayPurity(unit, metal) > 0

/**
 * Title-case metal label for inline purity cells (e.g. `'au'` → `'Au'`).
 * Distinct from chip-style `metalLabel` (`'AU'`).
 */
export const metalDisplayLabel = (metal) => {
  const key = String(metal).toLowerCase()
  return `${key.charAt(0).toUpperCase()}${key.slice(1)}`
}

/**
 * Ordered metals to show for a unit's purity cell: contract paidMetals first
 * (when displayable), then remaining purity keys sorted. Shared by picker and
 * manifest so paid-metal emphasis stays one rule.
 */
export const unitDisplayPurityMetals = (unit, paidMetals) => {
  const seen = new Set()
  const ordered = []
  const push = (metal) => {
    const key = String(metal).toLowerCase()
    if (seen.has(key) || !hasDisplayablePurity(unit, key)) return
    seen.add(key)
    ordered.push(key)
  }
  for (const metal of paidMetals || []) push(metal)
  for (const metal of Object.keys(unit?.purity || {}).sort()) push(metal)
  return ordered
}

// ============================================================================
// Settlement-doc icon
// ----------------------------------------------------------------------------
// Shared by `MasterLotSettlementCard` (read-side) and `MasterLotSettlementDialog`
// (write-side). Keep the supported MIME list in sync with
// `MASTER_LOT_SETTLEMENT_DOC_ALLOWED_MIME_TYPES` — if we admit a new type
// upstream, this dictionary is the only place that picks its icon.
// ============================================================================

export const settlementDocIcon = (mimeType) => {
  if (mimeType === 'application/pdf') return 'mdi-file-pdf-box'
  if (typeof mimeType === 'string' && mimeType.startsWith('image/')) return 'mdi-file-image'
  return 'mdi-file-outline'
}

// ============================================================================
// Settlement-disposition labels
// ----------------------------------------------------------------------------
// Canonical display labels for `SETTLEMENT_DISPOSITION_MODES`. Single source
// of truth so the settlement dialog (picker) and settlement card (chip)
// render identical text for the same mode.
//
// Labels are intentionally terse (chip-friendly). Picker surfaces may
// augment with parenthetical hints — e.g. dialog appends "(default)" to
// cash_invoice — locally; that's a per-surface UX concern, not data.
// ============================================================================

export const SETTLEMENT_DISPOSITION_LABELS = Object.freeze({
  cash_invoice: 'Cash invoice',
  held_on_partner_account: 'Held on partner account',
  transfer_out: 'Transfer out',
  physical_delivery: 'Physical delivery',
})

export const settlementDispositionLabel = (mode) =>
  SETTLEMENT_DISPOSITION_LABELS[mode] || String(mode)
